// The order's Ship action on aldoo.com: the waybill (товарителница) made with the courier from here —
// the courier picked, what the order says about the recipient, the office or address, the weight and
// the cash on delivery looked over (and put right), the courier asked to check it, then to make it.
// Its number is the parcel's tracking number: the order goes on its way and the buyer is mailed
// (commerce/lib/shipments). A courier the company has no account with here: the number by hand
// (`onManual`, the order screen's own form).
//
// ShipmentPanel: the waybill once made, on the order's Delivery tab — its label (A6 / A4) and its
// cancellation at the courier, while the courier does not have the parcel yet.
import React, { useEffect, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { TruckIcon, BuildingStorefrontIcon, ArchiveBoxIcon, PrinterIcon, CheckCircleIcon, LinkIcon } from "@heroicons/react/24/outline"
import { Chip, Thumb, Toggle } from "../AdminShared/ui"
import { OrderShipment } from "../AdminShared/api"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import { ConnectDialog } from "./ShippingProviders"

const METHOD_ICONS = { address: TruckIcon, office: BuildingStorefrontIcon, locker: ArchiveBoxIcon }
const money = (amount, currency = "EUR", locale = "bg-BG") => {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(Number(amount) || 0)
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`
  }
}

/** The label's PDF opened in a tab of its own (the browser prints or saves it): where printing in place fails. */
function openPdf(url, filename) {
  const tab = window.open(url, "_blank")
  if (tab) return
  const link = document.createElement("a")
  link.href = url
  link.download = filename || "label.pdf"
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * The label printed at once: its PDF loaded in a hidden frame of this page and the browser's print dialog
 * opened on it — no new tab. A browser that will not print a PDF that way gets the tab instead.
 */
function printPdf({ file, contentType, filename }) {
  const bytes = Uint8Array.from(atob(file), (c) => c.charCodeAt(0))
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType || "application/pdf" }))
  const frame = document.createElement("iframe")
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden"
  frame.src = url
  frame.onload = () => {
    try {
      frame.contentWindow.focus()
      frame.contentWindow.print()
    } catch {
      openPdf(url, filename)
    }
  }
  document.body.appendChild(frame)
  // the frame and its file go once the dialog is long done with them
  setTimeout(() => {
    frame.remove()
    URL.revokeObjectURL(url)
  }, 5 * 60 * 1000)
}

/** The label sizes a courier prints: the label printer's ("A6") and A4 for Speedy and Pigeon Express, one for the rest. */
export const labelSizes = (providerId) => (["speedy", "pigeon"].includes(providerId) ? ["A6", "A4"] : ["A6"])
/** A size as the courier makes it: Pigeon Express's label printer size is 100 × 90 mm. */
export const sizeName = (providerId, size) => (providerId === "pigeon" && size === "A6" ? "100×90" : size)

export async function printLabel(order, paperSize, t) {
  const res = await OrderShipment(order._id, "label", { paperSize }).catch((e) => ({ error: e.message }))
  if (!res?.file) return showNotification({ message: res?.message || t`The label could not be made`, className: "bg-admin_error text-white" })
  printPdf(res)
}

function LabelButtons({ order, t }) {
  const [busy, setBusy] = useState("")
  const print = async (size) => {
    setBusy(size)
    await printLabel(order, size, t)
    setBusy("")
  }
  // Econt makes its label in one size; Speedy and Pigeon Express print a label printer's size or A4
  const sizes = labelSizes(order.shipment?.providerId)
  return (
    <div className="flex flex-wrap gap-2">
      {sizes.map((size) => (
        <Button key={size} variant={size === "A6" ? "admin_primary" : "admin_secondary"} onClick={() => print(size)} disabled={!!busy} data-testid={`shipment-label-${size}`}>
          <span className="inline-flex items-center gap-2">
            {busy === size ? <Spinner variant={size === "A6" ? "white" : "admin_primary"} size="sm" /> : <PrinterIcon className="w-4 h-4" />}
            {sizes.length > 1 ? t("Print the sticker (%s)", sizeName(order.shipment?.providerId, size)) : t`Print the sticker`}
          </span>
        </Button>
      ))}
    </div>
  )
}

export function ShipmentPanel({ order, t, locale, onChanged }) {
  const s = order.shipment
  const [canceling, setCanceling] = useState(false)
  const cancel = async () => {
    const ok = await showPromptDialogAsync({
      variant: "admin",
      title: t`Cancel the waybill?`,
      message: t("Waybill %s is canceled at the courier, the tracking number taken off and the order open again. Only while the courier does not have the parcel yet.", s.number),
      confirmText: t`Cancel it`,
      cancelText: t`Keep it`,
    })
    if (!ok) return
    setCanceling(true)
    const res = await OrderShipment(order._id, "cancel").catch((e) => ({ error: e.message }))
    setCanceling(false)
    if (!res?.result) return showNotification({ message: res?.message || t`The courier did not cancel the waybill`, className: "bg-admin_error text-white" })
    showNotification({ message: t`Waybill canceled, the order is open again`, className: "bg-admin_success text-white" })
    onChanged?.()
  }
  const courier = { speedy: "Speedy", econt: "Econt" }[s.providerId] || s.providerId
  return (
    <div className="rounded-lg border border-admin_bordered p-4 flex flex-col gap-3" data-testid="shipment-panel">
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircleIcon className="w-5 h-5 text-admin_success" />
        <span className="text-black/50 uppercase text-[11px] tracking-wide">{t`Waybill`}</span>
        <span className="font-semibold">{courier} · {s.number}</span>
        {s.demo && <Chip className="bg-admin_warning/20 text-admin_text">{t`test system`}</Chip>}
      </div>
      <div className="text-sm text-admin_text_secondary flex flex-wrap gap-x-4 gap-y-1">
        {s.price != null && <span>{t("Courier: %s", money(s.price, s.currency || "EUR", locale))}</span>}
        <span>{s.payer === "RECIPIENT" ? t`The buyer pays the courier` : t`The company pays the courier`}</span>
        {s.codAmount > 0 && <span>{t("Cash on delivery: %s", money(s.codAmount, order.currency?.toUpperCase() || "EUR", locale))}</span>}
      </div>
      {/* the label first, centred; calling the waybill off apart from it, at the right, under a line */}
      <div className="flex justify-center py-2">
        <LabelButtons order={order} t={t} />
      </div>
      {order.orderStatus === "shipping" && (
        <div className="flex justify-end pt-3 border-t border-admin_bordered">
          <Button variant="admin_text" onClick={cancel} disabled={canceling} className="text-admin_error" data-testid="shipment-cancel">
            {canceling ? t`Canceling…` : t`Cancel the waybill`}
          </Button>
        </div>
      )}
    </div>
  )
}

/** A row of the waybill: what it is, and (`onEdit`) the button that changes it. */
function ReviewRow({ label, children, onEdit, editLabel, t }) {
  return (
    <div className="flex items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-admin_text_secondary">{label}</div>
        <div className="text-sm mt-0.5">{children}</div>
      </div>
      {onEdit && (
        <Button variant="admin_secondary" onClick={onEdit} className="shrink-0 h-9 px-4 text-sm" data-testid="shipment-review-change">
          {editLabel || t`Change`}
        </Button>
      )}
    </div>
  )
}

/** A choice of a radio group: the circle, what it is; picking it is the answer (`onPick`). */
function RadioRow({ selected, onPick, children, testId }) {
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onPick} className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${selected ? "border-admin_primary bg-admin_primary/5" : "border-admin_bordered hover:border-admin_primary"}`} data-testid={testId}>
      <span className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? "border-admin_primary" : "border-admin_neutral_2"}`}>
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-admin_primary" />}
      </span>
      <span className="flex-1 min-w-0">{children}</span>
    </button>
  )
}

/**
 * Where the parcel goes from: the addresses on the company's courier contract, one picked. A dialog of
 * its own, opened on demand (and by itself the first time one is needed); picking one closes it.
 */
export function SenderPicker({ senders, value, courierName, t, onPick, onClose }) {
  return (
    <Modal isOpen onClose={onClose} variant="admin" closeOnEscape title={t`Where it goes from`} styleOverrides={{ container: "w-[min(94vw,600px)] max-h-[85vh] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}>
      <div className="p-5 pt-1 flex flex-col gap-3" data-testid="sender-picker">
        <div className="text-sm text-admin_text_secondary">{t("Your offices registered with %s (the one chosen is used by default for the next orders)", courierName || "")}</div>
        <div className="flex flex-col gap-2" role="radiogroup">
          {senders.map((sender) => (
            <RadioRow key={sender.id} selected={sender.id === value} onPick={() => onPick(sender.id)} testId={`sender-${sender.id}`}>
              <span className="block text-sm font-semibold">{sender.address || sender.name}</span>
              <span className="block text-xs text-admin_text_secondary">{sender.name} · {sender.id}</span>
            </RadioRow>
          ))}
        </div>
      </div>
    </Modal>
  )
}

/**
 * No account with the courier yet: two ways on — connect the company's account (the same Connect dialog as
 * Delivery → courier; once connected, the waybill is made right here), or enter a tracking number from a waybill
 * made elsewhere. A courier whose waybills are not made here offers the number only.
 */
function NoAccountDialog({ courier, t, onConnect, onClose, onManual }) {
  const tc = usePortalTexts(COMPANY)
  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin"
      closeOnEscape
      title={t`Ship with a waybill`}
      styleOverrides={{ container: "w-[min(94vw,520px)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
    >
      <div className="px-5 pb-6 pt-1 flex flex-col items-center gap-5 text-center" data-testid="account-dialog">
        {courier?.carrier?.logo ? <img src={cmsUrl(courier.carrier.logo)} alt={courier.name} className="h-10 w-auto max-w-[160px] object-contain mt-2" /> : <TruckIcon className="w-12 h-12 text-admin_primary mt-2" />}
        <p className="m-0 max-w-[400px] text-sm text-admin_text_secondary">
          {courier?.supported ? tc("Connect your %s account to make the waybill from here, or enter the tracking number of a waybill you made yourself.", courier.name) : t("Waybills with %s are not made here yet: send it on your own and enter the number.", courier?.name || "")}
        </p>
        {/* the number first, connecting the account under it */}
        <div className="w-full max-w-[340px] flex flex-col gap-4 mt-2 mb-2">
          <Button variant="admin_secondary" onClick={onManual} className="w-full !h-12 !text-base" data-testid="account-manual">
            {tc`I have a tracking number`}
          </Button>
          {courier?.supported && (
            <Button variant="admin_primary" onClick={onConnect} className="w-full !h-12 !text-base" data-testid="account-connect">
              <span className="inline-flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                {tc("Connect %s", courier.name)}
              </span>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// One page: where it goes from first (the seller's choice, picked in its own dialog), then the waybill as
// the order has it — the buyer's courier, way, office or address and recipient, the parcel, the money —
// and the courier's answer: whether it accepts it, at what price.
export default function ShipmentDialog({ order, t, locale, onClose, onShipped, onManual }) {
  const tc = usePortalTexts(COMPANY)
  const [data, setData] = useState(null)
  const [draft, setDraft] = useState(null)
  const [busy, setBusy] = useState("")
  const [checked, setChecked] = useState(null)
  const [error, setError] = useState("")
  const [made, setMade] = useState(null)
  const [picking, setPicking] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  // the Connect dialog open (from the no-account choice); `connected`: it went through
  const [connecting, setConnecting] = useState(false)
  const connected = useRef(false)
  // the seller says the two addresses are right before the waybill is made (asked anew when the sender changes)
  const [verified, setVerified] = useState(false)

  const load = () =>
    OrderShipment(order._id, "draft").then((res) => {
      if (res?.error) return setError(res.message || res.error)
      setData(res)
      setDraft((d) => ({ ...res.draft, ...(d?.senderId && res.senders?.some((x) => x.id === d.senderId) ? { senderId: d.senderId } : {}) }))
      return res
    }).catch((e) => setError(e.message))

  useEffect(() => {
    load().then((res) => {
      if (!res || res.error) return
      const c = res.couriers?.find((x) => x.id === res.draft.providerId)
      // what is missing opens by itself the first time: the courier account, else where it goes from
      if (c && !c.canCreate) setAccountOpen(true)
      else if ((res.senders || []).length > 1 && !res.draft.senderId) setPicking(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order._id])

  const courier = data?.couriers?.find((c) => c.id === draft?.providerId)
  const method = courier?.methods?.find((m) => m.id === draft?.method) || null
  const pickup = !!method?.pickup
  const senders = data?.senders || []
  const sender = senders.find((x) => x.id === draft?.senderId)
  const needsAccount = !!courier && !courier.canCreate
  const deliveryProblem = !courier ? "courier" : pickup ? (!draft?.officeId ? "office" : null) : !draft?.address?.city?.trim() || !draft?.address?.line1?.trim() ? "address" : null
  const recipientProblem = !draft?.recipient?.name?.trim() || String(draft?.recipient?.phone || "").replace(/\D/g, "").length < 6
  // the courier can be asked once nothing is missing
  const askable = !!draft && !needsAccount && !deliveryProblem && !recipientProblem && (senders.length <= 1 || !!draft.senderId)

  const run = async (op) => {
    setBusy(op)
    setError("")
    const res = await OrderShipment(order._id, op, { draft }).catch((e) => ({ error: e.message }))
    setBusy("")
    if (!res?.result) return setError(res?.message || res?.error || t`The courier did not accept it`)
    if (op === "check") return setChecked(res)
    setMade(res.shipment)
    showNotification({ message: t("Waybill %s made, the buyer was told", res.shipment.number), className: "bg-admin_success text-white" })
  }

  // the courier asked at once, and again when where it goes from changes
  useEffect(() => {
    if (!askable || made) return
    setChecked(null)
    setVerified(false)
    run("check")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askable, draft?.senderId])

  const close = () => (made ? onShipped?.() : onClose?.())
  const footer = made ? (
    <div className="flex justify-end">
      <Button variant="admin_primary" onClick={close}>{t`Done`}</Button>
    </div>
  ) : (
    // on a phone one on top of the other (the main one first), side by side from 640px
    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
      <Button variant="admin_text" onClick={onManual} disabled={!!busy} className="w-full sm:w-auto" data-testid="shipment-manual">{t`I have a number already`}</Button>
      <Button variant="admin_primary" onClick={() => run("create")} disabled={!askable || !checked || !verified || !!busy} className="w-full sm:w-auto" data-testid="shipment-create">
        <span className="inline-flex items-center gap-2">
          {busy === "create" && <Spinner variant="white" size="sm" />}
          {busy === "create" ? t`Making the waybill…` : t`Make the waybill`}
        </span>
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={close}
      variant="admin"
      closeOnEscape={!busy}
      title={made ? t`Waybill made` : t`Ship with a waybill`}
      // as tall as what it holds (the buttons right under the route), up to the screen; centred on it
      styleOverrides={{ container: "relative w-[min(96vw,760px)] max-h-[92vh] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
      footerContent={footer}
    >
      <div className={`flex flex-col ${!data && !error ? "min-h-[220px]" : ""}`} data-testid="shipment-dialog">
        {/* the waybill's start asked for: a loader in the middle of the dialog */}
        {!data && !error && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 text-sm text-admin_text_secondary pointer-events-none" data-testid="shipment-loading">
            <Spinner variant="admin_primary" size="lg" />
            {t`Loading…`}
          </div>
        )}
        {/* the waybill being made: a loader in the middle of the dialog, over the route */}
        {busy === "create" && (
          <div className="absolute inset-0 z-30 bg-admin_paper/90 flex flex-col items-center justify-center gap-3 text-sm text-admin_text_secondary" data-testid="shipment-creating">
            <Spinner variant="admin_primary" size="lg" />
            {t`Creating your waybill…`}
          </div>
        )}
        {made && (
          <div className="flex flex-col items-center gap-3 text-center py-6" data-testid="shipment-made">
            <CheckCircleIcon className="w-14 h-14 text-admin_success" />
            <div className="text-lg font-semibold">{courier?.name} · {made.number}</div>
            <div className="text-sm text-admin_text_secondary max-w-[460px]">{t`The order is on its way and the buyer got the tracking number. Print the label and stick it on the parcel.`}</div>
            {made.demo && <Chip className="bg-admin_warning/20 text-admin_text">{t`Econt's test system: nothing is really sent`}</Chip>}
            <LabelButtons order={{ ...order, shipment: made }} t={t} />
            <span className="text-xs text-admin_text_secondary">{t`The label stays available on the order's Delivery tab`}</span>
          </div>
        )}
        {/* the route, as a picture: from the company, by the courier (its price beside the truck), to the buyer */}
        {draft && !made && (
          <div className="flex flex-col md:flex-row items-stretch gap-3 md:gap-0 py-4" data-testid="shipment-route">
            {/* from: the seller's own choice (its account first, when there is none yet) */}
            <div className="flex-1 min-w-0 rounded-xl border border-admin_bordered p-4 flex flex-col gap-2" data-testid="shipment-from">
              <div className="text-[11px] uppercase tracking-wide text-admin_text_secondary">{t`Sender`}</div>
              {needsAccount ? (
                <span className="text-sm text-admin_error">{t`Not given yet: waybills are made with the company's own account`}</span>
              ) : (
                <span className="text-sm font-semibold leading-snug">{sender ? sender.address || sender.name : senders.length ? <span className="text-admin_error font-normal">{t`Not chosen`}</span> : t`The company's address at the courier`}</span>
              )}
              <div className="mt-auto pt-1">
                {needsAccount ? (
                  <Button variant="admin_secondary" onClick={() => setAccountOpen(true)} className="h-9 px-4 text-sm">{tc`Connect`}</Button>
                ) : senders.length > 1 ? (
                  <Button variant="admin_secondary" onClick={() => setPicking(true)} className="h-9 px-4 text-sm" data-testid="shipment-sender-change">{sender ? t`Change` : t`Choose`}</Button>
                ) : null}
              </div>
            </div>

            {/* the courier between them: its logo, the truck, and its price once it has answered */}
            <div className="md:w-[180px] shrink-0 flex md:flex-col items-center justify-center gap-2 px-2" data-testid="shipment-courier">
              {courier?.carrier?.logo ? <img src={cmsUrl(courier.carrier.logo)} alt={courier.name} className="h-6 w-auto max-w-[100px] object-contain" /> : <span className="text-sm font-semibold">{courier?.name}</span>}
              <div className="flex items-center gap-2 w-full justify-center">
                <span className="hidden md:block flex-1 border-t-2 border-dashed border-admin_bordered" />
                <span className="w-12 h-12 rounded-full bg-admin_primary/10 flex items-center justify-center shrink-0">
                  <TruckIcon className="w-7 h-7 text-admin_primary" />
                </span>
                <span className="hidden md:block flex-1 border-t-2 border-dashed border-admin_bordered" />
              </div>
              <div className="h-6 flex items-center" data-testid="shipment-price">
                {busy === "check" ? (
                  <Spinner variant="admin_primary" size="sm" />
                ) : checked?.price != null ? (
                  <span className="text-base font-bold" title={draft.payer === "RECIPIENT" ? t`Paid by the buyer` : t`Paid by the company`}>{money(checked.price, checked.currency || "EUR", locale)}</span>
                ) : checked ? (
                  <CheckCircleIcon className="w-5 h-5 text-admin_success" />
                ) : null}
              </div>
            </div>

            {/* to: the buyer, as they chose */}
            <div className="flex-1 min-w-0 rounded-xl border border-admin_bordered p-4 flex flex-col gap-2" data-testid="shipment-to">
              <div className="text-[11px] uppercase tracking-wide text-admin_text_secondary">{t`Recipient`}</div>
              <span className="text-sm font-semibold">{draft.recipient.name}</span>
              {draft.recipient.phone && <span className="text-sm">{draft.recipient.phone}</span>}
              <span className="text-sm text-admin_text_secondary leading-snug">
                {pickup ? draft.office?.name || (method ? t(method.label) : "") : [draft.address.line1, draft.address.line2, [draft.address.postCode, draft.address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
              </span>
            </div>
          </div>
        )}
        {/* the seller has looked at both addresses: only then the waybill is made */}
        {draft && !made && askable && (
          <div className="flex justify-center pb-2" data-testid="shipment-verified">
            <Toggle checked={verified} onChange={(on) => setVerified(!!on)} label={t`I have checked the addresses of both the sender and the recipient`} disabled={!!busy} />
          </div>
        )}
        {draft && !made && deliveryProblem === "courier" && (
          <div className="mt-3 rounded-lg px-4 py-3 text-sm bg-admin_warning/15 text-admin_text" data-testid="shipment-courier-off">{t`The buyer's courier is not on for the company: turn it on in Delivery, or send it on your own and enter the number.`}</div>
        )}
        {draft && !made && !deliveryProblem && recipientProblem && (
          <div className="mt-3 rounded-lg px-4 py-3 text-sm bg-admin_warning/15 text-admin_text">{t`The order has no recipient's name or phone: the courier needs both. Send it on your own and enter the number.`}</div>
        )}
        {error && <div className="mt-3 rounded-lg px-4 py-3 text-sm bg-admin_error/10 text-admin_error" data-testid="shipment-error">{error}</div>}
        {!checked && !busy && error && askable && (
          <Button variant="admin_secondary" onClick={() => run("check")} className="mt-3 self-start">{t`Check again`}</Button>
        )}
      </div>
      {picking && (
        <SenderPicker
          senders={senders}
          value={draft?.senderId}
          courierName={courier?.name}
          t={t}
          onClose={() => setPicking(false)}
          onPick={(id) => {
            setDraft((d) => ({ ...d, senderId: id }))
            setPicking(false)
          }}
        />
      )}
      {accountOpen && courier && (
        <NoAccountDialog
          courier={courier}
          t={t}
          onClose={() => setAccountOpen(false)}
          onManual={() => {
            setAccountOpen(false)
            onManual?.()
          }}
          onConnect={() => {
            setAccountOpen(false)
            connected.current = false
            setConnecting(true)
          }}
        />
      )}
      {/* connected here: the waybill is made right after, in this dialog */}
      {connecting && courier && (
        <ConnectDialog
          orgId={order.sellerId}
          provider={courier}
          askLocation={false}
          doneLabel={tc`Continue to the waybill`}
          onConnected={() => {
            connected.current = true
          }}
          onClose={async () => {
            setConnecting(false)
            if (!connected.current) return setAccountOpen(true)
            const res = await load()
            // the account in: where it goes from is asked next, when the contract has several addresses
            if (res && !res.error && (res.senders || []).length > 1 && !res.draft.senderId) setPicking(true)
          }}
        />
      )}
    </Modal>
  )
}
